import { useEffect, useReducer, useRef, useState, useCallback, useMemo } from "react";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import { useNavigate, useLocation, Link } from "react-router-dom";
import ChartGrid from "../ChartGrid/ChartGrid";
import LoadingModal from "../LoadingModal";
import styles from './Benchmarking.module.css';
import SegmentedButtons from "../SegmentedButtons/SegmentedButtons";
import ButtonBar from "../ButtonBar/ButtonBar";
import { getColor } from "../../helpers/utils";
import ModalSurveyQuestion from "../ModalSurveyQuestion/ModalSurveyQuestion";
import ChartGridContext from "../../contexts/ChartGridContext";
import Tour from "../Tour/Tour";
import { useDispatch, useSelector } from "react-redux";
import {
	selectShowBenchmarkingTour,
	selectShowProfileDialog,
	setBenchmarkingTourShown,
	setBenchmarkingDialogShown,
	selectBenchmarkingTourShown,
	selectProfileDialogShown,
	selectBenchmarkingDialogShown,
	selectCanShowPopupQuestionDialog,
	setIsShowingBenchmarkingTour,
	setIsShowingProfileDialog,
	setIsShowingBenchmarkingDialog,
	setIsShowingPopupQuestionDialog,
	selectInvitationSourced
} from "../../redux/authSlice";
import BenchmarkingWizardDialog from "./BenchmarkingWizardDialog";
import useIdleDetection from "../../hooks/useIdleDetection";
import ScrollIndicator from "../ScrollIndicator/ScrollIndicator";

const ALL_BENCHMARKS = "All Benchmarks";

const reducer = (state, action) => {

	switch (action.type) {
		case ACTION.NEW_DATA:
			let refs = {}
			let newSurveys = [];
			for (let survey of action.payload.surveys) {
				refs[survey.surveyId] = newSurveys.length;
				newSurveys.push({
					surveyId: survey.surveyId,
					title: survey.title,
					questions: []
				});
			}
			for (let question of action.payload.questions) {
				newSurveys[refs[question.surveyId]].questions.push(question);
			}

			const buttonBarOptions = [ALL_BENCHMARKS];
			const buttonBarColors = [getColor(0)];
			for (let survey of newSurveys) {
				if (survey.questions.length > 0) {
					buttonBarOptions.push(survey.title);
					buttonBarColors.push(getColor(buttonBarOptions.length));
				}
				survey.answered = 0;
				for (let question of survey.questions) {
					if (question.answered) {
						survey.answered++;
					}
				}
				if (survey.answered === 0) {
					survey.linkText = "Begin survey";
				} else if (survey.answered === survey.questions.length) {
					survey.linkText = "Re-take survey";
				} else {
					survey.linkText = "Continue survey";
				}
			}
			return { ...state, surveys: [...newSurveys], buttonBarColors: [...buttonBarColors], buttonBarOptions: [...buttonBarOptions], loaded: true, };
		case ACTION.TOGGLE_PEERS_ONLY:
			return {
				...state,
				surveys: state.surveys.map(survey => {
					if (survey.surveyId === action.payload.surveyId) {
						return {
							...survey,
							questions: survey.questions.map(question =>
								question.surveyQuestionId === action.payload.surveyQuestionId
									? { ...question, peersOnly: action.payload.peersOnly, responses: [...action.payload.responses] }
									: question
							)
						}
					} else {
						return survey;
					}
				})
			};
		case ACTION.UPDATE_CHART_DATA:
			return {
				...state,
				surveys: state.surveys.map(survey => {
					if (survey.surveyId === action.payload.surveyId) {
						return {
							...survey,
							questions: survey.questions.map(question =>
								question.surveyQuestionId === action.payload.surveyQuestionId
									? action.payload
									: question
							)
						}
					} else {
						return survey;
					}
				})
			};
		case ACTION.LOADING:
			return { ...state, loaded: false };
		default:
			console.log(`unhandled action.type: ${action.type}`)
	}
}

const ACTION = {
	NEW_DATA: "newData",
	TOGGLE_PEERS_ONLY: "togglePeersOnly",
	UPDATE_CHART_DATA: "updateChartData",
	LOADING: "loading"
}

const Benchmarking = () => {

	const axiosPrivate = useAxiosPrivate();

	const reduxDispatch = useDispatch();

	// const profileDialogVisible = useSelector(selectProfileDialogVisible);

	const shouldShowBenchmarkingTour = useSelector(selectShowBenchmarkingTour);
	const shouldShowProfileDialog = useSelector(selectShowProfileDialog);
	const shouldShowBenchmarkingDialog = true;

	const benchmarkingTourShown = useSelector(selectBenchmarkingTourShown);
	const profileDialogShown = useSelector(selectProfileDialogShown);
	const benchmarkingDialogShown = useSelector(selectBenchmarkingDialogShown);
	const canShowPopupQuestionDialog = useSelector(selectCanShowPopupQuestionDialog);

	const invitationSourced = useSelector(selectInvitationSourced);

	const navigate = useNavigate();
	const location = useLocation();

	const toggleRef = useRef(null);
	const filterRef = useRef(null);
	const takeSurveyRef = useRef(null);
	const retakeQuestionRef = useRef(null);
	const peersRef = useRef(null);

	const [state, dispatch] = useReducer(reducer, {
		surveys: [],
		buttonBarOptions: [],
		buttonBarColors: [],
		loaded: false,
	});

	const handleSegmentedControlClick = (newValue) => {
		navigate("/insights");
	};

	const containerRef = useRef(null);

	const [selectedBenchmarkId, setSelectedBenchmarkId] = useState(0);
	const [selectedBenchmark, setSelectedBenchmark] = useState(ALL_BENCHMARKS);
	const [selectedSurveyQuestionId, setSelectedSurveyQuestionId] = useState(null);

	const [tourPositionsCalculated, setTourPositionsCalculated] = useState(false);
	const [spotlightDimensions, setSpotlightDimensions] = useState({ width: 0, height: 0, left: 0, top: 0 });
	const [tourPosition, setTourPosition] = useState({ left: 0, top: 0, position: "fixed" });

	const [tourStep, setTourStep] = useState(1);
	const [showSpotlight, setShowSpotlight] = useState(false);

	const [showBenchmarkingTour, setShowBenchmarkingTour] = useState(false);
	const [showBenchmarkingDialog, setShowBenchmarkingDialog] = useState(false);

	const handleUserIdle = () => {
		if (canShowPopupQuestionDialog && !invitationSourced) {
			stopIdle();
			reduxDispatch(setIsShowingPopupQuestionDialog(true));
		}
	};
	const stopIdle = useIdleDetection(handleUserIdle);

	const handleBenchmarkingChange = (benchmark) => {
		let surveyId = 0;
		for (let survey of state.surveys) {
			if (survey.title === benchmark) {
				surveyId = survey.surveyId;
				break;
			}
		}
		setSelectedBenchmarkId(surveyId);
		setSelectedBenchmark(benchmark);
	};

	const apiUrl = useRef(null);

	useEffect(() => {

		const url = "/benchmarking";

		let isMounted = true;

		const controller = new AbortController();

		const getData = async (url) => {

			try {
				const response = await axiosPrivate.get(url,
					{
						signal: controller.signal
					});
				if (isMounted) {
					dispatch({
						type: ACTION.NEW_DATA,
						payload: response.data
					});
				}
			} catch (err) {
				if (isMounted) {
					console.error(err);
					if (err.response?.status === 403) {
						navigate('/login', { state: { from: location }, replace: true });
					}
				}
			}
		}

		dispatch({
			type: ACTION.LOADING
		});

		apiUrl.current = url;

		getData(url);

		return () => {
			isMounted = false;
			controller.abort();
		}

	}, [axiosPrivate, navigate, location])

	const updateTourPositions = useCallback(() => {

		const padding = 8;

		let
			leftOffset,
			topOffset;

		if (containerRef.current) {
			const containerRect = containerRef.current.getBoundingClientRect();
			leftOffset = containerRect.left;
			topOffset = containerRect.top;
		}

		if (tourStep === 1) {
			setTourPosition(prevPosition => ({ ...prevPosition, position: "fixed" }));
			setTourPositionsCalculated(true);
			setShowSpotlight(false);
		} else if (toggleRef.current && (tourStep === 2)) {
			const rect = toggleRef.current.getBoundingClientRect();
			setSpotlightDimensions({ top: rect.top - padding - topOffset, left: rect.left - padding - leftOffset, width: rect.width + padding * 2, height: rect.height + padding * 2 });
			setShowSpotlight(true);
			setTourPosition({ left: rect.left - padding - leftOffset, top: rect.top + rect.height + 16 - topOffset, position: "absolute" });
			setTourPositionsCalculated(true);
		} else if (filterRef.current && (tourStep === 3)) {
			const rect = filterRef.current.getBoundingClientRect();
			setSpotlightDimensions({ top: rect.top - padding - topOffset, left: rect.left - padding - leftOffset, width: rect.width + padding * 2, height: rect.height + padding * 2 });
			setShowSpotlight(true);
			setTourPosition({ left: rect.left - padding - leftOffset, top: rect.top + rect.height + 16 - topOffset, position: "absolute" });
			setTourPositionsCalculated(true);
		} else if (takeSurveyRef.current && (tourStep === 4)) {
			const rect = takeSurveyRef.current.getBoundingClientRect();
			setSpotlightDimensions({ top: rect.top - padding - topOffset, left: rect.left - padding - leftOffset, width: rect.width + padding * 2, height: rect.height + padding * 2 });
			setShowSpotlight(true);
			setTourPosition({ left: rect.left - padding - leftOffset, top: rect.top + rect.height + 16 - topOffset, position: "absolute" });
			setTourPositionsCalculated(true);
		} else if (retakeQuestionRef.current && (tourStep === 5)) {
			const rect = retakeQuestionRef.current.getBoundingClientRect();
			setSpotlightDimensions({ top: rect.top - padding - topOffset, left: rect.left - padding - leftOffset, width: rect.width + padding * 2, height: rect.height + padding * 2 });
			setShowSpotlight(true);
			setTourPosition({ left: rect.left - padding - leftOffset, top: rect.top + rect.height + 16 - topOffset, position: "absolute" });
			setTourPositionsCalculated(true);
		} else if (peersRef.current && (tourStep === 6)) {
			const rect = peersRef.current.getBoundingClientRect();
			setSpotlightDimensions({ top: rect.top - padding - topOffset, left: rect.left - padding - leftOffset, width: rect.width + padding * 2, height: rect.height + padding * 2 });
			setShowSpotlight(true);
			setTourPosition({ left: rect.left - padding - leftOffset, top: rect.top + rect.height + 16 - topOffset, position: "absolute" });
			setTourPositionsCalculated(true);
		}
	}, [toggleRef, filterRef, takeSurveyRef, retakeQuestionRef, peersRef, tourStep]);


	useEffect(() => {
		if (state.loaded) {
			const timeout = setTimeout(() => {
				updateTourPositions();
			}, 50);
			return () => clearTimeout(timeout);
		}
	}, [state.loaded, updateTourPositions]);

	useEffect(() => {
		if (state.loaded) {
			if (shouldShowBenchmarkingTour && !benchmarkingTourShown) {
				setShowBenchmarkingTour(true);
				reduxDispatch(setIsShowingBenchmarkingTour(true));
			} else if (shouldShowProfileDialog && !profileDialogShown && !invitationSourced) {
				reduxDispatch(setIsShowingProfileDialog(true));
			} else if (shouldShowBenchmarkingDialog && !benchmarkingDialogShown && !invitationSourced) {
				setShowBenchmarkingDialog(true);
				reduxDispatch(setIsShowingBenchmarkingDialog(true));
			}
		}
	}, [state.loaded, shouldShowBenchmarkingTour, shouldShowProfileDialog, shouldShowBenchmarkingDialog, benchmarkingTourShown, profileDialogShown, benchmarkingDialogShown, reduxDispatch]);

	const showModalSurveyQuestion = useCallback((surveyQuestionId) => {
		setSelectedSurveyQuestionId(surveyQuestionId);
	}, []);

	const togglePeersOnly = useCallback(async (surveyQuestionId) => {

		let post = true;

		for (let survey of state.surveys) {
			for (let question of survey.questions) {
				if (question.surveyQuestionId === surveyQuestionId) {
					if (question.peersOnly) {
						post = false;
						break;
					}
				}
			}
			if (!post) {
				break;
			}
		}

		let request = post ? axiosPrivate.post : axiosPrivate.delete;

		try {
			const response = await request(`/profile/peersonly/${surveyQuestionId}`);
			dispatch({
				type: ACTION.TOGGLE_PEERS_ONLY,
				payload: response.data
			});
		} catch (err) {
			console.log(err);
		}
	}, [state.surveys]); // eslint-disable-line react-hooks/exhaustive-deps

	const updateChartResults = useCallback(async (surveyQuestionId) => {

		const controller = new AbortController();

		try {
			const response = await axiosPrivate.get(`/surveyQuestions/${surveyQuestionId}/results`,
				{
					signal: controller.signal
				});
			dispatch({
				type: ACTION.UPDATE_CHART_DATA,
				payload: response.data
			});
		} catch (err) {
			console.log(err);
		}
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	const closeModalSurveyQuestionDialog = () => {
		setSelectedSurveyQuestionId(null);
	}

	const chartGridContextValue = useMemo(() => ({
		showModalSurveyQuestion,
		togglePeersOnly,
		updateChartResults
	}), [showModalSurveyQuestion, togglePeersOnly, updateChartResults]);

	const nextTourStep = () => {
		setTourStep(prevStep => Math.min(prevStep + 1, 6));
	};

	const previousTourStep = () => {
		setTourStep(prevStep => Math.max(prevStep - 1, 1));
	};

	const closeTour = () => {
		reduxDispatch(setBenchmarkingTourShown());
		reduxDispatch(setIsShowingBenchmarkingTour(false));
		setShowBenchmarkingTour(false);
		if (shouldShowProfileDialog && !profileDialogShown) {
			reduxDispatch(setIsShowingProfileDialog(true));
		} else if (shouldShowBenchmarkingDialog && !benchmarkingDialogShown) {
			setShowBenchmarkingDialog(true);
			reduxDispatch(setIsShowingBenchmarkingDialog(true));
		}
	}

	const closeBenchmarkingDialog = () => {
		console.log("closeBenchmarkingDialog");
		reduxDispatch(setBenchmarkingDialogShown());
		reduxDispatch(setIsShowingBenchmarkingDialog(false));
		setShowBenchmarkingDialog(false);
	}

	if (!state.loaded) {
		return (
			<LoadingModal />
		)
	} else {
		const filteredSurveys = state.surveys.filter(survey =>
			((survey.questions.length > 0) && ((survey.surveyId === selectedBenchmarkId) || (selectedBenchmarkId === 0)))
		);
		return (

			<section className={styles.container} ref={containerRef}>
				<ScrollIndicator />
				<SegmentedButtons labels={["Benchmarking", "Insights"]} selectedValue={"Benchmarking"} width={600} onChange={handleSegmentedControlClick} toggleRef={toggleRef} />
				<ButtonBar labels={state.buttonBarOptions} selectedLabel={selectedBenchmark} colors={state.buttonBarColors} onClick={handleBenchmarkingChange} filterRef={filterRef} />
				<ChartGridContext.Provider value={chartGridContextValue}>
					{
						filteredSurveys.map((survey, i) => {
							return (
								<div key={i}>
									<div className={styles.surveyTitle}>
										<h1 className={styles.title}>{survey.title}</h1>
										<Link to={`/benchmarking/${survey.surveyId}`} {...(i === 0 ? { ref: takeSurveyRef } : {})} >{survey.linkText}</Link>
									</div>
									<ChartGrid chartData={survey.questions} {...(i === 0 ? { retakeQuestionRef: retakeQuestionRef, peersRef: peersRef } : {})} />
								</div>
							)
						})
					}
					{selectedSurveyQuestionId && <ModalSurveyQuestion surveyQuestionId={selectedSurveyQuestionId} onCloseDialog={closeModalSurveyQuestionDialog} />}
				</ChartGridContext.Provider>
				{
					(showBenchmarkingTour && tourPositionsCalculated) &&
					<Tour
						section="Benchmarking"
						step={tourStep}
						steps={6}
						onPrev={previousTourStep}
						onNext={nextTourStep}
						onClose={closeTour}
						top={tourPosition.top}
						left={tourPosition.left}
						position={tourPosition.position}
						showSpotlight={showSpotlight}
						spotlightDimensions={spotlightDimensions} />
				}
				{showBenchmarkingDialog &&
					<BenchmarkingWizardDialog onClose={closeBenchmarkingDialog} onResponse={updateChartResults} />
				}
			</section>
		)
	}

};

export default Benchmarking;